import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import { IQuestion } from '@/components/staffer/Grading/types';
import { applyGradeByStudentFilters } from '@/components/staffer/Grading/utils';
import I18n from '@/modules/i18n';
import ModelQuestionsContainer from '@/components/staffer/Grading/ModelQuestionsContainer';
import Text from '@/components/ui/Text';
import cn from '@/utils/cn';
import AISuggestionBanner from '@/components/staffer/Grading/AISuggestion/AISuggestionBanner';
import QuestionCard from './QuestionCards/QuestionCard';

// Exercise types whose cards show "Question N" instead of a section title, mirroring what the
// student sees. Reading Checks are deliberately absent — they show their section title.
const NUMBERED_EXERCISE_TYPES = ['Assessment', 'Challenge'];

const CardContainer = () => {
  const {
    assignment,
    isTeacher,
    questionAnswers,
  } = useGradingStore();
  const {
    assigned_model_types: assignedModelTypes,
    questions,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    grouped_questions,
    scoring_mode: scoringMode,
  } = assignment;
  const [filteredQuestions, setFilteredQuestions] = useState<IQuestion[]>(questions);
  const [groupIsExpanded, setGroupIsExpanded] = useState({});
  const [scrollToElement, setScrollToElement] = useState(false);
  const { filters } = useGradingStore.getState();
  const { autoGradedFilter, questionsFilter, statusFilter } = filters;
  const [searchParams] = useSearchParams();

  const shouldDisplayWarningBanner = assignedModelTypes.includes('Notebook') && scoringMode === 'legacy';
  const nonPromptQuestions = questions.filter(question => question.type !== 'PromptOnly');
  const suggestionBanner = isTeacher && <AISuggestionBanner questionAnswers={questionAnswers} unit="questions" />;

  useEffect(() => {
    if (!questions) return;

    setFilteredQuestions(
      isTeacher ? applyGradeByStudentFilters({
        autoGradedFilter,
        questionAnswers,
        questions,
        questionsFilter,
        statusFilter,
      }) : questions,
    );
  }, [autoGradedFilter, questionAnswers, questions, questionsFilter, statusFilter]);

  useEffect(() => {
    if (searchParams.get('defaultExpanded')) {
      const expanded = {};
      expanded[searchParams.get('defaultExpanded')] = true;
      setGroupIsExpanded(expanded);

      setScrollToElement(true);
    }
  }, [searchParams]);

  useEffect(() => {
    const elementToScrollTo = document.getElementById(`${searchParams.get('defaultExpanded')}-content`);
    if (!scrollToElement || !elementToScrollTo) return;

    elementToScrollTo.scrollIntoView({ behavior: 'smooth' });
    setScrollToElement(false);
  }, [scrollToElement]);

  if (!filteredQuestions || filteredQuestions.length < 1) {
    return (
      <p className="tw-mt-16 tw-w-full tw-font-semibold tw-text-center">
        {I18n.t('no_questions_to_grade')}
      </p>
    );
  }

  const ASSIGNMENT_TYPES_THAT_HAVE_GROUPING = ['SlideGroup', 'Notebook'];
  const shouldGroupQuestions = assignment
    .assigned_model_types
    .some(assigned_type => ASSIGNMENT_TYPES_THAT_HAVE_GROUPING.includes(assigned_type));

  if (shouldGroupQuestions) {
    const toggleExpanded = (keys: string[]) => {
      const groupsToToggle = {};
      keys.forEach((key: string) => { groupsToToggle[key] = !groupIsExpanded[key]; });
      setGroupIsExpanded({ ...groupIsExpanded, ...groupsToToggle });
    };

    const allExpanded = () => {
      if (Object.values(groupIsExpanded).length === 0) return false;

      return Object.values(groupIsExpanded).every(value => value === true);
    };

    const toggleAll = () => {
      const groupsToToggle = {};

      if (allExpanded()) {
        Object.keys(grouped_questions).forEach((key: string) => { groupsToToggle[key] = false; });
      } else {
        Object.keys(grouped_questions).forEach((key: string) => { groupsToToggle[key] = true; });
      }

      setGroupIsExpanded(groupsToToggle);
    };

    const allModelQuestions = Object.values(grouped_questions).flat();

    return (
      <>
        {suggestionBanner}
        <div className="tw-flex tw-mt-5 tw-items-center">
          {shouldDisplayWarningBanner && (
            <div
              className={cn(
                'tw-bg-teal-blue/5 tw-border tw-border-solid tw-border-teal-blue tw-text-teal-blue tw-py-1',
                'tw-px-5 tw-rounded-lg tw-box-border tw-text-center',
              )}
            >
              <Text size="sm">
                {I18n.t('assignment_warning')}
              </Text>
            </div>
          )}
          <button
            className="btn btn--outline-purple tw-ml-auto"
            type="button"
            onClick={toggleAll}
          >
            <i className={`tw-mr-2 fa ${allExpanded() ? 'fa-compress' : 'fa-expand'}`} />
            {allExpanded() ? 'Collapse' : 'Expand'} All Sections
          </button>
        </div>
        {
          Object.keys(grouped_questions).map((key) => {
            const modelTypeAndId = key.split('-');
            const modelQuestions = grouped_questions[key] || [];
            const filteredModelQuestions = modelQuestions.filter(q => filteredQuestions.some(q2 => q2.id === q.id));

            return (
              <ModelQuestionsContainer
                key={`model-questions-container-${key}`}
                allModelQuestions={allModelQuestions}
                isExpanded={groupIsExpanded[key]}
                modelId={modelTypeAndId[1]}
                modelType={modelTypeAndId[0]}
                questionAnswers={questionAnswers}
                questions={filteredModelQuestions}
                toggleExpanded={toggleExpanded}
              />
            );
          })
        }
      </>
    );
  }

  const isSlideShow = assignment.assigned_model_types.some(t => t === 'SlideShow');

  // AIDEV-NOTE: numbered off the *unfiltered* grouped_questions so grading filters never renumber
  // cards, and per assigned model so a mixed assignment doesn't count another model's questions.
  const questionNumbers = new Map<number, number>();
  Object.keys(grouped_questions).forEach((key) => {
    let questionNumber = 0;

    grouped_questions[key].forEach((q) => {
      if (['PageBreak', 'PromptOnly'].includes(q.type)) return;
      if (!NUMBERED_EXERCISE_TYPES.includes(q.question_group.exercise_type)) return;

      questionNumber += 1;
      questionNumbers.set(q.id, questionNumber);
    });
  });

  let ungroupedQuestions = Object
    .keys(grouped_questions)
    .flatMap(key => grouped_questions[key])
    .filter(q => filteredQuestions.some(q2 => q2.id === q.id));

  if (isSlideShow) {
    ungroupedQuestions = ungroupedQuestions.filter(question => question.type !== 'PromptOnly');
  }

  return (
    <>
      {suggestionBanner}
      {shouldDisplayWarningBanner && (
        <div
          className={cn(
            'tw-bg-teal-blue/5 tw-border tw-border-solid tw-border-teal-blue tw-text-teal-blue tw-py-1',
            'tw-px-5 tw-rounded-lg tw-box-border tw-text-center tw-max-w-[500px] tw-mt-5',
          )}
        >
          <Text size="sm">
            {I18n.t('assignment_warning')}
          </Text>
        </div>
      )}
      {ungroupedQuestions?.map((q) => {
        const questionAnswer = questionAnswers?.find(qa => qa.question_id.toString() === q.id.toString());
        const isReadingCheck = q.question_group.exercise_type === 'ReadingGame';

        let position: number | null = null;

        if (assignment.investigation) {
          const investigationIndex = nonPromptQuestions.findIndex(question => question.id === q.id);
          position = investigationIndex === -1 ? null : investigationIndex + 1;
        }

        if (isSlideShow && !isReadingCheck) {
          position = Number(q.display_position);
        }

        if (position === null && questionNumbers.has(q.id)) {
          position = questionNumbers.get(q.id);
        }

        return (
          <QuestionCard
            key={q.id}
            position={position}
            question={q}
            questionAnswer={questionAnswer}
          />
        );
      })}
    </>
  );
};

export default CardContainer;
